// @ts-strict-ignore
import React, {
	FC,
	forwardRef,
	ForwardRefExoticComponent,
	MouseEventHandler,
	RefAttributes,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react'

export type WithOpenCloseComponentProps<T = undefined> = {
	openPopup?: (evt) => void
	closePopup?: () => void
	popupIsClosing?: boolean
	popupIsOpened?: boolean
	forwardedRef?: RefObject<HTMLDivElement & T>
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const withOpenClose = <T extends any = unknown>(
	Component: FC<WithOpenCloseComponentProps<T>>,
	closeTimoutMs: number = 300
): ForwardRefExoticComponent<RefAttributes<T>> => {
	const WithOpenClose = ({ forwardedRef, ...props }: WithOpenCloseComponentProps<T>): JSX.Element => {
		const [popupIsOpened, setPopupIsOpened] = useState<boolean>(false)

		// Need to make fade out animation before popup element unmount
		const [popupIsClosing, setPopupIsClosing] = useState<boolean>(false)

		const openPopup: MouseEventHandler<HTMLButtonElement> = evt => {
			evt.preventDefault()
			setPopupIsOpened(true)
		}

		// keep a mutable ref to the latest close function so other callbacks don't need it
		const closePopupRef = useRef<() => void>(() => {})

		const onEscPress = useCallback((evt: KeyboardEvent): void => {
			if (evt.code === `Escape`) {
				closePopupRef.current()
			}
		}, [])

		const setCloseHandlers = useCallback((): void => {
			document.body.addEventListener(`keyup`, onEscPress)
		}, [onEscPress])

		const removeCloseHandlers = useCallback((): void => {
			document.body.removeEventListener(`keyup`, onEscPress)
		}, [onEscPress])

		const closePopup = useCallback((): void => {
			// Start closing animation
			setPopupIsClosing(true)

			removeCloseHandlers()

			setTimeout(() => {
				// Full closure
				setPopupIsOpened(false)
				setPopupIsClosing(false)
			}, closeTimoutMs - 50)
		}, [closeTimoutMs, removeCloseHandlers])

		useEffect(() => {
			closePopupRef.current = closePopup
		}, [closePopup])

		/**
		 * (Handlers are defined above as stable callbacks)
		 */

		// Set handlers on the popup opening
		useEffect(() => {
			if (popupIsOpened) {
				removeCloseHandlers()
				setCloseHandlers()
			}

			// Remove handlers on component unmount
			return () => removeCloseHandlers()
		}, [popupIsOpened, removeCloseHandlers, setCloseHandlers])
		//
		// // Clear possible existed handlers on mount
		useEffect(() => removeCloseHandlers(), [removeCloseHandlers])

		return (
			<Component
				forwardedRef={forwardedRef}
				popupIsOpened={popupIsOpened}
				popupIsClosing={popupIsClosing}
				openPopup={openPopup}
				closePopup={closePopup}
				{...props}
			/>
		)
	}

	return forwardRef((props, ref) => (
		<WithOpenClose {...props} forwardedRef={ref as unknown as RefObject<HTMLDivElement & T>} />
	))
}
